import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Pencil } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const blank = { name: "", email: "", password: "", role: "Sales" };

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = () => api.get("/users").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/roles").then((r) => setRoles(r.data)).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || (!editing && (!form.email.trim() || !form.password))) { toast.error("Fill all required fields"); return; }
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Team member updated");
      } else {
        await api.post("/users", form);
        toast.success("Team member added");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (u) => { try { await api.delete(`/users/${u.id}`); load(); toast.success("Removed"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const toggleActive = async (u) => { try { await api.put(`/users/${u.id}`, { active: !u.active }); load(); } catch (e) { toast.error("Update failed"); } };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div><h1 className="text-3xl font-heading font-bold tracking-tight">Team</h1><p className="text-muted-foreground mt-1">Add employees and assign roles.</p></div>
        <Button className="rounded-sm gap-2" onClick={openNew} data-testid="new-user-btn"><UserPlus size={16} /> Add Member</Button>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody data-testid="users-table">
            {rows.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm capitalize">{u.role}</Badge></TableCell>
                <TableCell><button onClick={() => toggleActive(u)}><Badge variant="outline" className={`rounded-sm ${u.active ? "bg-success/15 text-success border-success/30" : "bg-muted text-muted-foreground"}`}>{u.active ? "Active" : "Inactive"}</Badge></button></TableCell>
                <TableCell className="text-right space-x-2">
                  <button onClick={() => openEdit(u)} className="text-muted-foreground hover:text-primary" data-testid={`edit-user-${u.id}`}><Pencil size={15} /></button>
                  {u.id !== user?.id && <button onClick={() => remove(u)} className="text-muted-foreground hover:text-primary" data-testid={`del-user-${u.id}`}><Trash2 size={15} /></button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Member" : "Add Member"}</DialogTitle><DialogDescription>{editing ? "Update name, role or reset password." : "Create a login and assign a role."}</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5"><Label>Name *</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" /></div>
            <div className="grid gap-1.5"><Label>Email *</Label><Input data-testid="user-email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" /></div>
            <div className="grid gap-1.5"><Label>{editing ? "New password (optional)" : "Password *"}</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm" /></div>
            <div className="grid gap-1.5"><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-sm" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button><Button className="rounded-sm" onClick={save} data-testid="user-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
